import { useState, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Download,
  Eye,
  PenTool,
  Clock,
  CheckCircle,
  FileText,
} from "lucide-react";
import {
  assignmentService,
  documentService,
  downloadFile,
} from "@/services/documentService";
import { DocumentAssignment } from "@/types/document";
import { StatusBadge } from "@/components/ui/status-badge";
import { DocumentSigningModal } from "../../components/documents/DocumentSigningModal";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { useAuth } from "@/pages/auth/auth.context";

const UserDocumentDashboard = () => {
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<DocumentAssignment | null>(null);

  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Get current user ID from auth context
  const currentUserId = user?.id;

  // Fetch user's assignments
  const {
    data: assignmentsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["assignments", "user", currentUserId],
    queryFn: async () => {
      try {
        const filters = {
          myDocumentsOnly: true,
          page: 0,
          size: 1000, // Get all user assignments
        };

        const result = await assignmentService.getAssignments(filters);
        // Return the content array from paginated response
        return result.content || [];
      } catch (error) {
        console.error("Error fetching assignments:", error);
        throw error;
      }
    },
    enabled: !!currentUserId, // Only run query if user is logged in
  });

  // Ensure assignments is always an array
  const assignments = Array.isArray(assignmentsData) ? assignmentsData : [];

  const downloadDocumentMutation = useMutation({
    mutationFn: documentService.downloadDocument,
    onSuccess: (blob, documentId) => {
      const assignment = assignments.find((a) => a.documentId === documentId);
      if (assignment) {
        downloadFile(blob, `${assignment.documentTitle}.pdf`);
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to download document",
        variant: "destructive",
      });
    },
  });

  const handleSign = (assignment: DocumentAssignment) => {
    setSelectedAssignment(assignment);
    setShowSigningModal(true);
  };

  const handleDownload = (assignment: DocumentAssignment) => {
    downloadDocumentMutation.mutate(assignment.documentId);
  };

  const getAssignmentsByStatus = () => {
    // Ensure assignments is always an array before filtering
    if (!Array.isArray(assignments)) {
      return { pending: [], completed: [] };
    }

    const pending = assignments.filter(
      (a) => a.status === "PENDING" || a.status === "IN_PROGRESS",
    );
    const completed = assignments.filter((a) => a.status === "SIGNED");

    return { pending, completed };
  };

  const { pending, completed } = getAssignmentsByStatus();

  // Show loading state if user is not loaded yet
  if (!user && !error) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-destructive">
              Error loading your documents. Please try again.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My Documents</h1>
        <p className="text-muted-foreground">
          View and sign your assigned documents
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{assignments.length}</div>
                <div className="text-sm text-muted-foreground">Total</div>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-warning">
                  {pending.length}
                </div>
                <div className="text-sm text-muted-foreground">Pending</div>
              </div>
              <Clock className="h-8 w-8 text-warning" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-success">
                  {completed.length}
                </div>
                <div className="text-sm text-muted-foreground">Completed</div>
              </div>
              <CheckCircle className="h-8 w-8 text-success" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Documents */}
      {pending.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Pending Documents
            </CardTitle>
            <CardDescription>
              Documents that require your signature
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pending.map((assignment) => (
                <div
                  key={assignment.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      {assignment.documentTitle}
                    </div>
                    <div className="flex items-center gap-4 mt-2">
                      <StatusBadge status={assignment.status} />
                      <div className="text-sm text-muted-foreground">
                        Assigned{" "}
                        {format(new Date(assignment.assignedAt), "MMM d, yyyy")}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(assignment)}
                      disabled={downloadDocumentMutation.isPending}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleSign(assignment)}
                      className="bg-success hover:bg-success/90"
                    >
                      <PenTool className="h-4 w-4 mr-2" />
                      Sign
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completed Documents */}
      {completed.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Completed Documents
            </CardTitle>
            <CardDescription>
              Documents you have successfully signed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {completed.map((assignment) => (
                <div
                  key={assignment.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      {assignment.documentTitle}
                    </div>
                    <div className="flex items-center gap-4 mt-2">
                      <StatusBadge status={assignment.status} />
                      {assignment.signedAt && (
                        <div className="text-sm text-success">
                          Signed{" "}
                          {format(new Date(assignment.signedAt), "MMM d, yyyy")}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(assignment)}
                      disabled={downloadDocumentMutation.isPending}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {isLoading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </CardContent>
        </Card>
      ) : assignments.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground h-64 flex items-center justify-center">
              No documents assigned to you yet.
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Document Signing Modal */}
      {showSigningModal && selectedAssignment && (
        <DocumentSigningModal
          open={showSigningModal}
          assignment={selectedAssignment}
          onClose={() => {
            setShowSigningModal(false);
            setSelectedAssignment(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["assignments", "user", currentUserId],
            });
            setShowSigningModal(false);
            setSelectedAssignment(null);
          }}
        />
      )}
    </div>
  );
};

export default UserDocumentDashboard;
